import { useSyncExternalStore } from 'react'
import whatsNewData from 'data/whats-new.json'
import { WHATS_NEW_STORAGE_KEY, WHATS_NEW_VISIBLE_RELEASES } from 'app-constants'
import { safeLocalStorage } from 'helpers/localStorage'
import { IWhatsNewData, IWhatsNewRelease } from 'types'

// Storage may be unavailable (private window, blocked site data); safeLocalStorage
// swallows that and the badge simply stays on.
const readLastSeen = (): string | null => safeLocalStorage.getItem(WHATS_NEW_STORAGE_KEY)

// The sidebar badge and the panel are separate components that both call this hook, so the
// "last seen" value lives in localStorage rather than component state and every instance
// subscribes to the same change notifications.
const listeners = new Set<() => void>()

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const notify = (): void => {
  listeners.forEach((listener) => listener())
}

export interface WhatsNewStore {
  releases: IWhatsNewRelease[]
  hasUnread: boolean
  markSeen: () => void
}

export const useWhatsNew = (data: IWhatsNewData = whatsNewData as IWhatsNewData): WhatsNewStore => {
  const lastSeen = useSyncExternalStore(subscribe, readLastSeen, readLastSeen)

  // A release whose stories were all non-user-facing ships with an empty `entries` list: the
  // drafter keeps the block so its merge stays idempotent. There is nothing to show for one, so
  // drop it before slicing — otherwise it takes a visible slot from a real release, renders as a
  // date heading with no rows, and lights the unread badge for content that does not exist.
  const releases = data.releases.filter((release) => release.entries.length > 0).slice(0, WHATS_NEW_VISIBLE_RELEASES)
  const newestId = releases[0]?.id
  const hasUnread = Boolean(newestId) && newestId !== lastSeen

  const markSeen = (): void => {
    if (!newestId) return
    safeLocalStorage.setItem(WHATS_NEW_STORAGE_KEY, newestId)
    notify()
  }

  return { releases, hasUnread, markSeen }
}
